// Global 401 interceptor.
//
// Services call fetch() directly (no shared client), so to react to an expired
// session from ANY endpoint we patch window.fetch once. Whenever a request to
// OUR OWN API (apps/stac, same-origin /api/v1/* or SYDON_API_URL) responds 401,
// we emit a window event. A provider that owns the logout function listens for
// it and logs the user out.
//
// Deliberately excludes auth.sydon.ai (PropelAuth's own hosted API): its
// refresh_token endpoint returns 401 for every logged-out visitor as normal,
// expected behavior, not a "session expired mid-use" signal — treating it as
// one caused an infinite reload loop (401 -> logout -> reload -> 401 -> ...).
//
// The patch only READS the response status and always returns the original
// response untouched, wrapped in try/catch so it can never break a fetch.

import { SYDON_API_URL } from '../config/env';

export const UNAUTHORIZED_EVENT = 'sydon:unauthorized';

let installed = false;

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function isOwnApiUrl(url: string): boolean {
  if (url.startsWith('/api/v1/')) return true; // same-origin, proxied to apps/stac
  return url.startsWith(SYDON_API_URL) && url.includes('/api/v1/');
}

export function installUnauthorizedInterceptor(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const response = await originalFetch(...args);
    try {
      if (response.status === 401 && isOwnApiUrl(getUrl(args[0]))) {
        window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
      }
    } catch {
      // Never let session detection interfere with the actual request.
    }
    return response;
  };
}
